import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import axios from "axios";
import { API_ESA_OFFICE } from "utils";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 2 PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE_UPLOAD } from "utils";
import { logoutUser } from "actions/AuthAction";

function DaftarUsers() {
  const [users, setUsers] = useState([]);
  const [dinasId, setDinasId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [listDinas, setListDinas] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingDialog, setLoadingDialog] = useState(false);
  const [userStatus, setUserStatus] = useState("");
  const [selectedDinas, setSelectedDinas] = useState(null);
  const [dinasStruktur, setDinasStruktur] = useState(null);
  const [loadingDinas, setLoadingDinas] = useState(true);
  const [loadingStruktur, setLoadingStruktur] = useState(true);
  const [jabatanOptions, setJabatanOptions] = useState([]);
  const [bidangOptions, setBidangOptions] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isValidFile, setIsValidFile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    nama: "",
    email: "",
    nama_jabatan: "",
    pangkat: "",
    facebook: "",
    instagram: "",
    profil: null,
    instansi: "",
    jabatan: "",
    bidang: "",
    status: "",
    unit: "",
    bio: "",
    noHP: "",
    status_EO: "Aktif",
    // Field internal untuk keperluan backend (tidak ditampilkan)
    dinas_id: "",
    bidang_id: "",
    level_jabatan: "",
  });
  const navigate = useNavigate();
  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Fungsi untuk fetching struktur dinas
  const fetchStruktur = async (dinasId) => {
    if (!dinasId) return;

    setLoadingStruktur(true);
    try {
      const res = await axios.get(`${API_ESA_OFFICE}dinas/${dinasId}/struktur`);
      setDinasStruktur(res.data && res.data[0] ? res.data[0] : null);

      // Ambil opsi jabatan dari dinasStruktur jika ada
      const jabatanOptions =
        res.data[0] && Array.isArray(res.data[0].struktur)
          ? res.data[0].struktur.map((j) => ({
              value: j.nama_jabatan,
              label: j.nama_jabatan,
            }))
          : [];
      setJabatanOptions(jabatanOptions);
      // Ambil opsi bidang dari dinasStruktur jika ada
      const bidangOptions =
        res.data[0] && Array.isArray(res.data[0].bidang)
          ? res.data[0].bidang.map((b) => ({
              value: b.bidang_id,
              label: b.bidang,
            }))
          : [];
      setBidangOptions(bidangOptions);
    } catch (err) {
      setDinasStruktur(null);
    }
    setLoadingStruktur(false);
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      setUserStatus(user.status);
      fetchListDinas(user.dinas_id);
    } else {
      logoutUser(navigate);
    }
  }, []);

  useEffect(() => {
    if (dinasId) {
      fetchUsers();
    }
  }, [dinasId]);

  // Effect untuk menangani perubahan selectedDinas (hanya untuk reset form data)
  useEffect(() => {
    if (selectedDinas && selectedDinas.dinas_id) {
      fetchStruktur(selectedDinas.dinas_id);
      setFormData((prevData) => ({
        ...prevData,
        instansi: selectedDinas.nama_dinas,
        dinas_id: selectedDinas.dinas_id,
        // Reset jabatan dan bidang ketika dinas berubah
        jabatan: "",
        bidang: "",
        bidang_id: "",
        level_jabatan: "",
      }));
    }
  }, [selectedDinas]);

  // Fungsi untuk mengambil list dinas
  const fetchListDinas = async (dinasId) => {
    setLoadingDinas(true);
    try {
      const res = await axios.get(`${API_ESA_OFFICE}dinas`);
      const dinasData = res.data || [];
      const sepesifikDinas = dinasData.filter((item) => {
        return item.dinas_id == dinasId;
      });
      setListDinas(sepesifikDinas);
    } catch (err) {
      console.error("Error fetching list dinas:", err);
      setListDinas([]);
    }
    setLoadingDinas(false);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}users/dinas/${dinasId}`
      );
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function untuk mendapatkan label pangkat
  const getPangkatLabel = (value) => {
    const pangkatMap = {
      IA: "Juru Muda (I/a)",
      IB: "Juru Muda Tingkat I (I/b)",
      IC: "Juru (I/c)",
      ID: "Juru Tingkat I (I/d)",
      IIA: "Pengatur Muda (II/a)",
      IIB: "Pengatur Muda Tingkat I (II/b)",
      IIC: "Pengatur (II/c)",
      IID: "Pengatur Tingkat I (II/d)",
      IIIA: "Penata Muda (III/a)",
      IIIB: "Penata Muda Tingkat I (III/b)",
      IIIC: "Penata (III/c)",
      IIID: "Penata Tingkat I (III/d)",
      IVA: "Pembina (IV/a)",
      IVB: "Pembina Tingkat I (IV/b)",
      IVC: "Pembina Utama Muda (IV/c)",
      IVD: "Pembina Utama Madya (IV/d)",
    };
    return pangkatMap[value] || value;
  };

  const handleOpenDialog = async (user) => {
    if (!user) return;

    setLoadingDialog(true);
    setSelectedUser(user);

    // Reset upload state
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsValidFile(false);
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Set selectedDinas berdasarkan dinas_id user
    const userDinas = listDinas.find((d) => d.dinas_id === user.dinas_id);
    if (userDinas) {
      setSelectedDinas(userDinas);
      // Fetch struktur dinas terlebih dahulu sebelum membuka dialog
      await fetchStruktur(userDinas.dinas_id);
    }

    setFormData({
      nama: user.nama || "",
      email: user.email || "",
      nama_jabatan: user.nama_jabatan || "",
      pangkat: user.pangkat || "",
      facebook: user.facebook || "",
      instagram: user.instagram || "",
      profil: user.profil || null,
      instansi: user.nama_dinas || "",
      jabatan: user.nama_jabatan || "",
      bidang: user.nama_bidang || "",
      status: user.status || "",
      unit: user.jenis_unit || "",
      bio: user.bio || "",
      noHP: user.noHP || "",
      status_EO: user.status_EO || "Aktif",
      // Field internal untuk keperluan backend (tidak ditampilkan)
      dinas_id: user.dinas_id || "",
      bidang_id: user.bidang_id || "",
      level_jabatan: user.level_jabatan || "",
    });

    setLoadingDialog(false);
    setOpenDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, profil: file });
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];

    // Cleanup preview URL sebelumnya jika ada
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    if (!file) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsValidFile(false);
      return;
    }

    // Validasi ukuran file (maksimal 1MB)
    if (file.size > 1 * 1024 * 1024) {
      swal({
        title: "Error",
        text: "Ukuran file melebihi 1 MB. Silakan pilih file yang lebih kecil",
        icon: "error",
      });
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsValidFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Validasi tipe file
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      swal({
        title: "Error",
        text: "Format file tidak didukung. Hanya JPG dan PNG yang diperbolehkan",
        icon: "error",
      });
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsValidFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // File valid
    setSelectedFile(file);
    setIsValidFile(true);

    // Buat preview URL menggunakan createObjectURL untuk efisiensi
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
  };

  const handleUploadAvatar = async () => {
    if (!selectedFile || !selectedUser?.uid_RSS) {
      swal({
        title: "Error",
        text: "Silakan pilih gambar terlebih dahulu",
        icon: "error",
      });
      return;
    }

    setUploading(true);
    try {
      // Buat File object baru dengan nama yang diubah menjadi uid_RSS
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
      const validExtension = ["jpg", "jpeg", "png"].includes(fileExtension)
        ? fileExtension === "jpeg"
          ? "jpg"
          : fileExtension
        : "jpg"; // Default ke jpg jika extension tidak valid

      // Buat File baru dengan nama uid_RSS.{extension}
      const renamedFile = new File(
        [selectedFile],
        `${selectedUser.uid_RSS}.${validExtension}`,
        {
          type: selectedFile.type,
          lastModified: selectedFile.lastModified,
        }
      );

      const formData = new FormData();
      formData.append("photo", renamedFile);

      const uploadResponse = await axios.post(
        `https://smartdh.rotendaokab.go.id/api/users/upload-avatar`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (uploadResponse.data && uploadResponse.data.success === 1) {
        // Cleanup preview URL sebelum close
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }

        swal({
          title: "Berhasil!",
          text:
            uploadResponse.data.message || "Gambar profil berhasil diupdate",
          icon: "success",
          button: "OK",
        });

        // Reset state
        setSelectedFile(null);
        setPreviewUrl(null);
        setIsValidFile(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Refresh users list untuk mendapatkan gambar baru
        await fetchUsers();
      } else {
        throw new Error(
          uploadResponse.data?.message || "Gagal mengupload gambar"
        );
      }
    } catch (error) {
      console.error("Error uploading avatar:", error);
      swal({
        title: "Error!",
        text: error.response?.data?.message || "Gagal mengupload gambar profil",
        icon: "error",
        button: "OK",
      });
    } finally {
      setUploading(false);
    }
  };

  // Ambil opsi jabatan dari dinasStruktur jika ada

  const handleEdit = async () => {
    if (!selectedUser) return;

    try {
      // Susun payload JSON tanpa mengirim gambar (profil)
      const {
        nama,
        email,
        nama_jabatan,
        jabatan,
        level_jabatan,
        dinas_id,
        bidang_id,
        bidang,
        pangkat,
        status,
        facebook,
        instagram,
        unit,
        noHP,
        status_EO,
      } = formData;

      // Backend PUT membutuhkan: nama, email, nama_jabatan, level_jabatan, dinas_id, bidang_id, pangkat, status, facebook, instagram, noHP, status_EO
      const payload = {
        nama,
        email,
        nama_jabatan: nama_jabatan || jabatan || "",
        level_jabatan: level_jabatan || "",
        dinas_id: dinas_id || selectedDinas?.dinas_id || null,
        bidang_id: bidang_id || null,
        pangkat: pangkat || null,
        status: status || "user",
        facebook: facebook || null,
        instagram: instagram || null,
        noHP: noHP || null,
        status_EO: status_EO || "Aktif",
      };

      await axios.put(
        `${API_ESA_OFFICE}users/${selectedUser.user_id}`,
        payload
      );
      swal({
        title: "Berhasil!",
        text: "Data user berhasil diperbarui",
        icon: "success",
        button: "OK",
      });
      setOpenDialog(false);
      fetchUsers();
    } catch (error) {
      swal({
        title: "Error!",
        text:
          error.response?.data?.message ||
          "Terjadi kesalahan saat memperbarui data",
        icon: "error",
        button: "OK",
      });
    }
  };

  const handleDelete = (userId) => {
    swal({
      title: "Apakah Anda yakin?",
      text: "Data user akan dihapus secara permanen!",
      icon: "warning",
      buttons: ["Batal", "Ya, Hapus!"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await axios.delete(`${API_ESA_OFFICE}users/${userId}`);
          swal({
            title: "Berhasil!",
            text: "User berhasil dihapus",
            icon: "success",
            button: "OK",
          });
          fetchUsers();
        } catch (error) {
          swal({
            title: "Error!",
            text:
              error.response?.data?.message ||
              "Terjadi kesalahan saat menghapus user",
            icon: "error",
            button: "OK",
          });
        }
      }
    });
  };

  const baseColumns = [
    {
      Header: "No",
      accessor: "no",
      width: "5%",
      Cell: ({ row }) => row.index + 1,
    },
    {
      Header: "Profil",
      accessor: "profil",
      width: "8%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center">
          {row.original.profil ? (
            <img
              src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
              alt={row.original.nama}
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
              }}
            />
          ) : (
            <Icon sx={{ fontSize: 40, color: "text.secondary" }}>person</Icon>
          )}
        </MDBox>
      ),
    },
    { Header: "Nama", accessor: "nama", width: "15%" },
    { Header: "Email", accessor: "email", width: "18%" },
    { Header: "Jabatan", accessor: "nama_jabatan", width: "15%" },
    {
      Header: "Bidang",
      accessor: "nama_bidang",
      width: "200px",
      Cell: ({ value }) => (
        <MDBox
          sx={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            maxWidth: "200px",
            py: 1,
          }}
        >
          <MDTypography variant="caption" color="text">
            {value || "-"}
          </MDTypography>
        </MDBox>
      ),
    },
    { Header: "Pangkat", accessor: "pangkat", width: "8%" },
    { Header: "No HP", accessor: "noHP", width: "10%" },
    {
      Header: "Status",
      accessor: "status",
      width: "8%",
      Cell: ({ value }) => (
        <MDTypography
          variant="caption"
          fontWeight="medium"
          color={
            value === "admin"
              ? "success"
              : value === "user"
              ? "info"
              : value === "super admin"
              ? "warning"
              : value === "pimpinan"
              ? "warning"
              : "text"
          }
        >
          {value && typeof value === "string" ? value.toUpperCase() : "-"}
        </MDTypography>
      ),
    },
    {
      Header: "Status EO",
      accessor: "status_EO",
      width: "10%",
      Cell: ({ value }) => (
        <MDTypography
          variant="caption"
          fontWeight="medium"
          color={
            value === "Aktif"
              ? "success"
              : value === "Non Aktif"
              ? "error"
              : value === "Pensiun"
              ? "warning"
              : value === "Meninggal"
              ? "error"
              : "text"
          }
        >
          {value || "Aktif"}
        </MDTypography>
      ),
    },
    {
      Header: "Facebook",
      accessor: "facebook",
      width: "15%",
      Cell: ({ value }) => (
        <MDBox display="flex" alignItems="center">
          {value && (
            <MDBox
              component="a"
              href={`https://facebook.com/${value}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "#1877F2",
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              <Icon sx={{ fontSize: 24, mr: 1 }}>facebook</Icon>
              <MDTypography variant="caption" fontWeight="medium">
                {value}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      ),
    },
    // Kolom Aksi akan ditambahkan secara kondisional di bawah
  ];

  const actionColumn = {
    Header: "Aksi",
    accessor: "aksi",
    width: "8%",
    Cell: ({ row }) => (
      <MDBox display="flex" alignItems="center" gap={1}>
        <MDButton
          variant="text"
          color="info"
          size="small"
          onClick={() => handleOpenDialog(row.original)}
          disabled={loadingDialog}
          sx={{ minWidth: "auto", p: 0.5 }}
        >
          {loadingDialog ? <CircularProgress size={16} /> : <Icon>edit</Icon>}
        </MDButton>
        <MDButton
          variant="text"
          color="error"
          size="small"
          onClick={() => handleDelete(row.original.user_id)}
          sx={{ minWidth: "auto", p: 0.5 }}
        >
          <Icon>delete</Icon>
        </MDButton>
      </MDBox>
    ),
  };

  const columns =
    userStatus === "admin" || userStatus === "super admin"
      ? [...baseColumns, actionColumn]
      : baseColumns;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <MDTypography variant="h4">Daftar Users</MDTypography>
            </Grid>
            <Grid item>
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => navigate("/team/new-user")}
              >
                <Icon>add</Icon>&nbsp;Tambah User
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>

        {isMobile ? (
          <MDBox display="flex" flexDirection="column" gap={1}>
            {loading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={4}
              >
                <CircularProgress size={40} />
                <MDTypography variant="body2" color="text" ml={2}>
                  Memuat data pengguna...
                </MDTypography>
              </MDBox>
            ) : (
              <>
                {users.map((user, index) => (
                  <Card key={`${user.user_id || "user"}-${index}`}>
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4}>
                          <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {user.profil ? (
                              <img
                                src={`https://smartdh.rotendaokab.go.id/uploads/${user.uid_RSS}.jpg`}
                                alt={user.nama}
                                style={{
                                  width: 72,
                                  height: 72,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  border: "2px solid #e0e0e0",
                                }}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  const sibling = e.currentTarget.nextSibling;
                                  if (sibling) sibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <MDBox
                              display={user.profil ? "none" : "flex"}
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                width: 72,
                                height: 72,
                                borderRadius: "50%",
                                backgroundColor: "grey.200",
                                color: "grey.500",
                              }}
                            >
                              <Icon sx={{ fontSize: 36 }}>person</Icon>
                            </MDBox>
                          </MDBox>
                        </Grid>
                        <Grid item xs={8}>
                          <MDTypography variant="button" fontWeight="bold">
                            {user.nama}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {user.email || "-"}
                          </MDTypography>
                          <MDBox
                            mt={1}
                            display="flex"
                            flexWrap="wrap"
                            gap={0.5}
                          >
                            {user.nama_jabatan ? (
                              <Chip
                                label={`Jabatan: ${user.nama_jabatan}`}
                                size="small"
                                color="info"
                              />
                            ) : null}
                            {user.nama_bidang ? (
                              <Chip
                                label={`Bidang: ${user.nama_bidang}`}
                                size="small"
                                color="secondary"
                              />
                            ) : null}
                            {user.pangkat ? (
                              <Chip
                                label={`Pangkat: ${getPangkatLabel(
                                  user.pangkat
                                )}`}
                                size="small"
                                color="warning"
                              />
                            ) : null}
                            {user.status ? (
                              <Chip
                                label={`Status: ${user.status}`}
                                size="small"
                                color={
                                  user.status === "admin"
                                    ? "success"
                                    : user.status === "user"
                                    ? "info"
                                    : user.status === "super admin" ||
                                      user.status === "pimpinan"
                                    ? "warning"
                                    : "default"
                                }
                              />
                            ) : null}
                            {user.noHP ? (
                              <Chip
                                label={`HP: ${user.noHP}`}
                                size="small"
                                color="info"
                              />
                            ) : null}
                            {user.status_EO ? (
                              <Chip
                                label={`Status EO: ${user.status_EO}`}
                                size="small"
                                color={
                                  user.status_EO === "Aktif"
                                    ? "success"
                                    : user.status_EO === "Non Aktif" ||
                                      user.status_EO === "Meninggal"
                                    ? "error"
                                    : user.status_EO === "Pensiun"
                                    ? "warning"
                                    : "default"
                                }
                              />
                            ) : null}
                          </MDBox>
                          {(userStatus === "admin" ||
                            userStatus === "super admin") && (
                            <MDBox mt={1} display="flex" gap={1}>
                              <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                onClick={() => handleOpenDialog(user)}
                                sx={{ minWidth: "auto", px: 1 }}
                              >
                                <Icon sx={{ fontSize: 16 }}>edit</Icon>
                                &nbsp;Edit
                              </MDButton>
                              <MDButton
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => handleDelete(user.user_id)}
                                sx={{ minWidth: "auto", px: 1 }}
                              >
                                <Icon sx={{ fontSize: 16 }}>delete</Icon>
                                &nbsp;Hapus
                              </MDButton>
                            </MDBox>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </MDBox>
        ) : (
          <>
            {loading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={4}
              >
                <CircularProgress size={40} />
                <MDTypography variant="body2" color="text" ml={2}>
                  Memuat data pengguna...
                </MDTypography>
              </MDBox>
            ) : (
              <DataTable
                table={{
                  columns,
                  rows: users,
                }}
                entriesPerPage={false}
                showTotalEntries={false}
                isSorted={false}
                noEndBorder
              />
            )}
          </>
        )}

        <Dialog
          open={openDialog}
          onClose={() => {
            // Cleanup preview URL saat dialog ditutup
            if (previewUrl) {
              URL.revokeObjectURL(previewUrl);
            }
            setOpenDialog(false);
            setSelectedFile(null);
            setPreviewUrl(null);
            setIsValidFile(false);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <MDTypography variant="h4" align="center" fontWeight="bold">
              Edit User - {selectedUser?.nama || ""}
            </MDTypography>
          </DialogTitle>
          <DialogContent>
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              mb={4}
            >
              <MDBox position="relative" display="inline-block">
                <MDBox
                  component="img"
                  src={
                    previewUrl ||
                    (selectedUser?.profil
                      ? `https://smartdh.rotendaokab.go.id/uploads/${selectedUser.uid_RSS}.jpg`
                      : "https://via.placeholder.com/150")
                  }
                  alt="Profile"
                  sx={{
                    width: 150,
                    height: 150,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "4px solid #fff",
                    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                  }}
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/150";
                  }}
                />
                {userStatus === "super admin" && (
                  <MDBox
                    component="label"
                    htmlFor="profil-upload-avatar"
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      cursor: "pointer",
                      backgroundColor: "info.main",
                      color: "white",
                      borderRadius: "50%",
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        backgroundColor: "info.dark",
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: 20 }}>edit</Icon>
                  </MDBox>
                )}
              </MDBox>
              {userStatus === "super admin" && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="profil-upload-avatar"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                  />
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mt={1}
                    textAlign="center"
                  >
                    Format: JPG, PNG | Maksimal: 1 MB
                  </MDTypography>
                  {selectedFile && (
                    <MDBox mt={1} textAlign="center">
                      <MDTypography variant="caption" color="text.secondary">
                        File: {selectedFile.name} (
                        {(selectedFile.size / 1024).toFixed(2)} KB)
                      </MDTypography>
                    </MDBox>
                  )}
                  {isValidFile && (
                    <MDButton
                      variant="gradient"
                      color="info"
                      size="small"
                      onClick={handleUploadAvatar}
                      disabled={uploading}
                      sx={{ mt: 2 }}
                      startIcon={
                        uploading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : null
                      }
                    >
                      {uploading ? "Mengupload..." : "Upload Foto"}
                    </MDButton>
                  )}
                </>
              )}
            </MDBox>

            {/* Akun RSS Section */}
            <MDBox mb={3}>
              <MDTypography variant="h6" fontWeight="bold" color="primary">
                Akun Rote Smart Service
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Informasi akun RSS user
              </MDTypography>
            </MDBox>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDInput
                  type="email"
                  label="Email Address"
                  fullWidth
                  value={formData.email}
                />
              </Grid>
            </Grid>

            {/* Info User Section */}
            <MDBox mb={3} mt={4}>
              <MDTypography variant="h6" fontWeight="bold" color="primary">
                Informasi User
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Data pribadi dan jabatan user
              </MDTypography>
            </MDBox>

            <Grid container spacing={3}>
              <Grid item xs={12} md={12}>
                <MDInput
                  type="text"
                  label="Nama Lengkap"
                  fullWidth
                  value={formData.nama}
                  onChange={(e) =>
                    setFormData({ ...formData, nama: e.target.value })
                  }
                  placeholder="Nama lengkap beserta gelar"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={1} ml={0.5}>
                  <MDTypography
                    component="label"
                    variant="caption"
                    fontWeight="regular"
                    color="text"
                  >
                    Instansi
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  value={selectedDinas}
                  onChange={(event, newValue) => {
                    setSelectedDinas(newValue);
                  }}
                  options={listDinas}
                  getOptionLabel={(option) => option.nama_dinas || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.dinas_id === value?.dinas_id
                  }
                  loading={loadingDinas}
                  disabled={loadingDinas}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      placeholder="Pilih Dinas"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.dinas_id}>
                      {option.nama_dinas}
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={1} ml={0.5}>
                  <MDTypography
                    component="label"
                    variant="caption"
                    fontWeight="regular"
                    color="text"
                  >
                    Jabatan
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  value={
                    formData.jabatan
                      ? {
                          value: formData.jabatan,
                          label: formData.jabatan,
                        }
                      : null
                  }
                  onChange={(event, newValue) => {
                    const selectedJabatan = newValue?.value || "";
                    let level = "";
                    if (
                      dinasStruktur &&
                      Array.isArray(dinasStruktur.struktur)
                    ) {
                      const found = dinasStruktur.struktur.find(
                        (j) => j.nama_jabatan === selectedJabatan
                      );
                      if (found) level = found.level_jabatan;
                    }
                    setFormData({
                      ...formData,
                      nama_jabatan: selectedJabatan,
                      jabatan: selectedJabatan,
                      level_jabatan: level,
                    });
                  }}
                  options={jabatanOptions}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value?.value
                  }
                  loading={loadingStruktur}
                  disabled={loadingStruktur}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      placeholder="Pilih Jabatan"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={1} ml={0.5}>
                  <MDTypography
                    component="label"
                    variant="caption"
                    fontWeight="regular"
                    color="text"
                  >
                    Bidang
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  value={
                    formData.bidang
                      ? {
                          value: formData.bidang_id,
                          label: formData.bidang,
                        }
                      : null
                  }
                  onChange={(event, newValue) => {
                    const selectedBidangId = newValue?.value || "";
                    const selectedBidang =
                      dinasStruktur && Array.isArray(dinasStruktur.bidang)
                        ? dinasStruktur.bidang.find(
                            (b) => b.bidang_id == selectedBidangId
                          )
                        : null;
                    setFormData({
                      ...formData,
                      bidang_id: selectedBidangId,
                      bidang: selectedBidang ? selectedBidang.bidang : "",
                    });
                  }}
                  options={bidangOptions}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value?.value
                  }
                  loading={loadingStruktur}
                  disabled={loadingStruktur}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      placeholder="Pilih Bidang"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={1} ml={0.5}>
                  <MDTypography
                    component="label"
                    variant="caption"
                    fontWeight="regular"
                    color="text"
                  >
                    Pangkat
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  value={
                    formData.pangkat
                      ? {
                          value: formData.pangkat,
                          label: getPangkatLabel(formData.pangkat),
                        }
                      : null
                  }
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      pangkat: newValue?.value || "",
                    });
                  }}
                  options={[
                    { value: "IA", label: "Juru Muda (I/a)" },
                    { value: "IB", label: "Juru Muda Tingkat I (I/b)" },
                    { value: "IC", label: "Juru (I/c)" },
                    { value: "ID", label: "Juru Tingkat I (I/d)" },
                    { value: "IIA", label: "Pengatur Muda (II/a)" },
                    { value: "IIB", label: "Pengatur Muda Tingkat I (II/b)" },
                    { value: "IIC", label: "Pengatur (II/c)" },
                    { value: "IID", label: "Pengatur Tingkat I (II/d)" },
                    { value: "IIIA", label: "Penata Muda (III/a)" },
                    { value: "IIIB", label: "Penata Muda Tingkat I (III/b)" },
                    { value: "IIIC", label: "Penata (III/c)" },
                    { value: "IIID", label: "Penata Tingkat I (III/d)" },
                    { value: "IVA", label: "Pembina (IV/a)" },
                    { value: "IVB", label: "Pembina Tingkat I (IV/b)" },
                    { value: "IVC", label: "Pembina Utama Muda (IV/c)" },
                    { value: "IVD", label: "Pembina Utama Madya (IV/d)" },
                  ]}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value?.value
                  }
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      placeholder="Pilih Pangkat/Golongan"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={1} ml={0.5}>
                  <MDTypography
                    component="label"
                    variant="caption"
                    fontWeight="regular"
                    color="text"
                  >
                    Status
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  value={
                    formData.status
                      ? {
                          value: formData.status,
                          label: formData.status === "user" ? "User" : "Admin",
                        }
                      : null
                  }
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, status: newValue?.value || "" });
                  }}
                  options={[
                    { value: "user", label: "User" },
                    { value: "admin", label: "Admin" },
                  ]}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value?.value
                  }
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      placeholder="Pilih Status"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDInput
                  type="select"
                  label="Unit"
                  fullWidth
                  value={formData.unit}
                  onChange={(e) =>
                    setFormData({ ...formData, unit: e.target.value })
                  }
                  options={[
                    { value: "", label: "Pilih Unit" },
                    { value: "Sekretariat", label: "Sekretariat" },
                    { value: "Sub Bagian", label: "Sub Bagian" },
                    { value: "Bidang", label: "Bidang" },
                    { value: "Seksi", label: "Seksi" },
                    { value: "UPT", label: "UPT" },
                    { value: "Lainnya", label: "Lainnya" },
                  ]}
                />
              </Grid>
            </Grid>

            {/* Socials Section */}
            <MDBox mb={3} mt={4}>
              <MDTypography variant="h6" fontWeight="bold" color="primary">
                Social Media
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Akun media sosial user
              </MDTypography>
            </MDBox>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MDInput
                  type="text"
                  label="No HP"
                  fullWidth
                  value={formData.noHP}
                  onChange={(e) =>
                    setFormData({ ...formData, noHP: e.target.value })
                  }
                  placeholder="Nomor HP/WhatsApp"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon>phone</Icon>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={1} ml={0.5}>
                  <MDTypography
                    component="label"
                    variant="caption"
                    fontWeight="regular"
                    color="text"
                  >
                    Status EO
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  value={
                    formData.status_EO
                      ? {
                          value: formData.status_EO,
                          label: formData.status_EO,
                        }
                      : { value: "Aktif", label: "Aktif" }
                  }
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      status_EO: newValue?.value || "Aktif",
                    });
                  }}
                  options={[
                    { value: "Aktif", label: "Aktif" },
                    { value: "Non Aktif", label: "Non Aktif" },
                    {
                      value: "Pindah Ke Luar Kabupaten",
                      label: "Pindah Ke Luar Kabupaten",
                    },
                    { value: "Meninggal", label: "Meninggal" },
                    { value: "Pensiun", label: "Pensiun" },
                    { value: "Dipecat", label: "Dipecat" },
                  ]}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value?.value
                  }
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      placeholder="Pilih Status EO"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDInput
                  type="text"
                  label="Facebook Account"
                  fullWidth
                  value={formData.facebook}
                  onChange={(e) =>
                    setFormData({ ...formData, facebook: e.target.value })
                  }
                  placeholder="Username Facebook"
                  InputProps={{
                    startAdornment: (
                      <Icon sx={{ color: "#1877F2", mr: 1 }}>facebook</Icon>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDInput
                  type="text"
                  label="Instagram Account"
                  fullWidth
                  value={formData.instagram}
                  onChange={(e) =>
                    setFormData({ ...formData, instagram: e.target.value })
                  }
                  placeholder="Username Instagram"
                  InputProps={{
                    startAdornment: (
                      <Icon sx={{ color: "#E1306C", mr: 1 }}>camera_alt</Icon>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* Profile Section */}
            <MDBox mb={3} mt={4}>
              <MDTypography variant="h6" fontWeight="bold" color="primary">
                Profile
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Informasi tambahan dan bio user
              </MDTypography>
            </MDBox>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDInput
                  type="text"
                  label="Bio"
                  fullWidth
                  multiline
                  rows={5}
                  value={formData.bio}
                  onChange={(e) =>
                    setFormData({ ...formData, bio: e.target.value })
                  }
                  placeholder="Tuliskan bio atau deskripsi singkat tentang user"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <MDButton
              onClick={() => {
                // Cleanup preview URL saat dialog ditutup
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                }
                setOpenDialog(false);
                setSelectedFile(null);
                setPreviewUrl(null);
                setIsValidFile(false);
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
              variant="outlined"
              color="secondary"
            >
              Batal
            </MDButton>
            <MDButton onClick={handleEdit} variant="gradient" color="info">
              Simpan Perubahan
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}

export default DaftarUsers;
